/* eslint-disable no-throw-literal */
import gql from "graphql-tag";
import apolloClient from "../../../../utils/apolloClient";

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const SEARCH_ORDER_INVOICE = gql`
  query SearchOrderInvoice($input: SearchInput) {
    search_order_invoice(input: $input) {
      total_pages
      total_orders
      orders_listing {
        application_fee
        credit_card_fees
        delivery_fee
        kravein_charges
        kravein_fees
        kravein_fee_gst
        kravein_payout
        net_payout
        order_date
        order_reference
        order_subtotal
        order_total
        place_name
        promo
        adjustment_amount
        sale_value
        service_type
        venue_charges
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const SERVICE_TYPES = new Set(["Pickup", "Delivery", "Dine-in", "Booking"]);

const assertServiceType = (val, orderRef) => {
  if (!val || !SERVICE_TYPES.has(val)) {
    console.warn(
      `[searchOrderInvoiceService] Unexpected service_type: "${val}" for "${orderRef}", defaulting to Pickup`,
    );
    return "Pickup";
  }
  return val;
};

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toOrderInvoice = raw => {
  const orderRef = raw?.order_reference ?? "unknown";
  return {
    orderReference: orderRef,
    orderDate: raw?.order_date ?? "",
    placeName: raw?.place_name ?? "",
    serviceType: assertServiceType(raw?.service_type, orderRef),
    applicationFee: raw?.application_fee ?? 0,
    orderSubtotal: raw?.order_subtotal ?? 0,
    promo: raw?.promo ?? 0,
    adjustmentAmount: raw?.adjustment_amount ?? 0,
    venueCharges: raw?.venue_charges ?? 0,
    kraveinCharges: raw?.kravein_charges ?? 0,
    saleValue: raw?.sale_value ?? 0,
    kraveinFees: raw?.kravein_fees ?? 0,
    kraveinFeeGst: raw?.kravein_fee_gst ?? 0,
    kraveinPayout: raw?.kravein_payout ?? 0,
    netPayout: raw?.net_payout ?? 0,
    deliveryFee: raw?.delivery_fee ?? 0,
    creditCardFees: raw?.credit_card_fees ?? 0,
    orderTotal: raw?.order_total ?? 0,
  };
};

const toOrderPage = raw => ({
  orders: Array.isArray(raw?.orders_listing)
    ? raw.orders_listing.filter(item => item !== null).map(toOrderInvoice)
    : [],
  totalPages: raw?.total_pages ?? 0,
  totalOrders: raw?.total_orders ?? 0,
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === "object" && err !== null && "code" in err && "message" in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes("unauthorized") || msg.includes("unauthenticated")) {
      return { message: err.message, code: "UNAUTHORIZED" };
    }
    if (msg.includes("network") || msg.includes("failed to fetch")) {
      return { message: err.message, code: "NETWORK_ERROR" };
    }
    return { message: err.message, code: "UNKNOWN_ERROR" };
  }
  return { message: "An unexpected error occurred", code: "UNKNOWN_ERROR" };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function searchOrderInvoices(
  params,
  client = apolloClient.clientPrivate,
) {
  const {
    userId,
    companyInvoiceId,
    from = 0,
    size = 15,
    sort = "DATE_ASC",
    search = "",
    serviceType = '',
  } = params;

  const orderInvoiceFilter = {};
  if (search) orderInvoiceFilter.order_number = search;
  if (serviceType && serviceType !== 'all') {
    orderInvoiceFilter.service_type = serviceType;
  }

  try {
    const { data } = await client.query({
      query: SEARCH_ORDER_INVOICE,
      variables: {
        input: {
          user_id: userId,
          company_invoice_id: companyInvoiceId,
          from,
          size,
          sort,
          ...(Object.keys(orderInvoiceFilter).length > 0
            ? { filter: { order_invoice_filter: orderInvoiceFilter } }
            : {}),
        },
      },
      fetchPolicy: "network-only",
    });

    const result = data?.search_order_invoice;

    if (!result) {
      throw {
        message: "No data returned from search_order_invoice",
        code: "PARSE_ERROR",
      };
    }

    if (!Array.isArray(result.orders_listing)) {
      throw {
        message: "No data returned from search_order_invoice",
        code: "PARSE_ERROR",
      };
    }

    return toOrderPage(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toOrderInvoice, toOrderPage };

// ─── Export Service ───────────────────────────────────────────────────────────

export const searchOrderInvoiceService = {
  searchOrderInvoices,
};
